import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

/**
 * Route-change transition overlay (ChainGPT-style) featuring the FMS distributor
 * hexagon icon glowing, pulsing and rotating. Plays briefly on every navigation.
 */
const PageTransition = () => {
  const location = useLocation();
  const [active, setActive] = useState(true);

  useEffect(() => {
    setActive(true);
    // Reset scroll to the top on every route change (hidden behind the overlay)
    window.scrollTo(0, 0);
    const t = setTimeout(() => setActive(false), 650);
    return () => clearTimeout(t);
  }, [location.pathname]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key={location.pathname}
          data-testid="page-transition-overlay"
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#0c0c0c] pointer-events-none"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ y: '-100%' }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="absolute inset-0 grid-bg opacity-40" />

          <motion.div
            className="relative flex flex-col items-center gap-5"
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <motion.div
              className="relative w-24 h-24 flex items-center justify-center"
              animate={{ rotate: [0, 10, -10, 0], scale: [0.92, 1.08, 0.92] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <svg viewBox="0 0 100 110" className="w-full h-full">
                <defs>
                  <linearGradient id="fmsHexGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#1E50A0" />
                    <stop offset="100%" stopColor="#F08A24" />
                  </linearGradient>
                </defs>
                <motion.polygon
                  points="50,4 92,29 92,81 50,106 8,81 8,29"
                  fill="rgba(30,80,160,0.12)"
                  stroke="url(#fmsHexGrad)"
                  strokeWidth="3"
                  strokeLinejoin="round"
                  animate={{
                    filter: [
                      'drop-shadow(0 0 6px rgba(30,80,160,0.6))',
                      'drop-shadow(0 0 22px rgba(240,138,36,0.85))',
                      'drop-shadow(0 0 6px rgba(30,80,160,0.6))',
                    ],
                  }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                />
              </svg>
              <img
                src="/assets/fms-logo.png"
                alt="FMSInc."
                className="absolute w-11 h-11 object-contain"
              />
            </motion.div>
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-[#F08A24]">
              FMSInc.
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PageTransition;
